import React, { useState, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import { connect } from 'react-redux';
import { Clipboard, Check2, ArrowClockwise, FileEarmark, Film } from 'react-bootstrap-icons';
import clsx from 'clsx';
import { resolveProfilePic } from '../utils/profile';

function normalizeRole(message) {
  if (message.role) return message.role;
  if (message.user === 'assistant') return 'assistant';
  return 'questioner';
}

function CodeBlock({ inline, className, children, ...props }) {
  const [copied, setCopied] = useState(false);
  const match = /language-(\w+)/.exec(className || '');
  const language = match?.[1] || '';
  const code = String(children).replace(/\n$/, '');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  if (inline) {
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  }

  return (
    <div className="group/code relative my-3 overflow-hidden rounded-lg border border-border-subtle/50 bg-[#0d1117]">
      <div className="flex items-center justify-between border-b border-border-subtle/40 bg-surface px-3 py-1.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-text-muted">
          {language || 'code'}
        </span>
        <button
          type="button"
          onClick={handleCopy}
          className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-text-muted hover:bg-surface-hover hover:text-text-primary"
        >
          {copied ? <Check2 size={12} /> : <Clipboard size={12} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="m-0 overflow-x-auto">
        <code className={clsx(className, 'hljs')} {...props}>
          {children}
        </code>
      </pre>
    </div>
  );
}

const ChatMessage = ({
  message,
  user,
  isLastAssistant,
  onRegenerate,
  showThinking,
}) => {
  const role = normalizeRole(message);
  const isAssistant = role === 'assistant';
  const isStreaming = message.streaming && isAssistant;
  const userAvatar = resolveProfilePic(user?.profile_pic_url);
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(message.message || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  }, [message.message]);

  // A streaming placeholder with no text yet is the same state as "thinking",
  // so it renders the dots rather than an empty bubble with a bare caret.
  if (showThinking || (isStreaming && !message.message)) {
    return (
      <div className="mx-auto flex w-full max-w-chat gap-3 px-4 py-4">
        <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-white">
          SC
        </div>
        <div className="flex items-center gap-1.5 pt-2">
          <span className="thinking-dot h-2 w-2 rounded-full bg-text-muted" />
          <span className="thinking-dot h-2 w-2 rounded-full bg-text-muted" />
          <span className="thinking-dot h-2 w-2 rounded-full bg-text-muted" />
        </div>
      </div>
    );
  }

  if (!isAssistant) {
    return (
      <div className="group mx-auto flex w-full max-w-chat justify-end gap-3 px-4 py-3">
        <div className="flex max-w-[85%] flex-col items-end gap-2">
          {message.attachments?.length > 0 && (
            <div className="flex flex-wrap justify-end gap-2">
              {message.attachments.map((att, i) => (
                <AttachmentBadge key={`${att.name}-${i}`} att={att} />
              ))}
            </div>
          )}
          {message.message ? (
            <div className="rounded-2xl rounded-br-md bg-surface-elevated px-4 py-2.5 text-[15px] leading-relaxed text-white whitespace-pre-wrap">
              {message.message}
            </div>
          ) : null}
          <div className="flex opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
            <ActionButton
              onClick={handleCopy}
              label={copied ? 'Copied' : 'Copy'}
              icon={copied ? <Check2 size={14} /> : <Clipboard size={14} />}
            />
          </div>
        </div>
        <img
          src={userAvatar}
          alt="You"
          className="mt-1 h-8 w-8 shrink-0 rounded-full object-cover bg-surface-elevated"
        />
      </div>
    );
  }

  return (
    <div className="group mx-auto flex w-full max-w-chat gap-3 px-4 py-4">
      <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-white">
        SC
      </div>
      <div className="min-w-0 flex-1">
        {message.attachments?.length > 0 && (
          <div className="mb-2 flex flex-wrap gap-2">
            {message.attachments.map((att, i) => (
              <AttachmentBadge key={`${att.name}-${i}`} att={att} />
            ))}
          </div>
        )}
        <div className="prose-chat text-[15px]">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            components={{ code: CodeBlock }}
          >
            {message.message || ''}
          </ReactMarkdown>
          {isStreaming && <span className="streaming-cursor" />}
        </div>
        {!isStreaming && message.message && (
          <div className="mt-2 flex gap-1 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
            <ActionButton
              onClick={handleCopy}
              label={copied ? 'Copied' : 'Copy'}
              icon={copied ? <Check2 size={14} /> : <Clipboard size={14} />}
            />
            {isLastAssistant && onRegenerate && (
              <ActionButton
                onClick={onRegenerate}
                label="Regenerate"
                icon={<ArrowClockwise size={14} />}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function AttachmentBadge({ att }) {
  const isImage = att.mime?.startsWith('image/') || att.preview;
  const isPdf = att.mime === 'application/pdf';
  if (isImage && (att.preview || att.data)) {
    return (
      <img
        src={att.preview || att.data}
        alt={att.name}
        className="h-16 w-16 rounded-lg object-cover border border-border-subtle"
      />
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg bg-surface-elevated px-2.5 py-1.5 text-xs text-text-primary">
      {isPdf ? <FileEarmark size={14} className="text-accent" /> : <Film size={14} className="text-accent" />}
      <span className="max-w-[140px] truncate">{att.name}</span>
    </span>
  );
}

function ActionButton({ onClick, label, icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs text-text-muted hover:bg-surface-hover hover:text-text-primary"
      title={label}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps)(ChatMessage);
